import { useInkind } from '@gik/api/inkinds/inkind';
import { useWishlistProducts } from '@gik/api/inkinds/wishlistProducts';
import { useInkindCan } from '@gik/core/store/permissions';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { RemoveFromWishlistButton } from '@gik/ui/gik/RemoveFromWishlistButton';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { Tooltip } from '@gik/ui/Tooltip';
import TrashIcon from '@heroicons/react/solid/TrashIcon';
import React from 'react';
import SeeFullWishlistButton from './SeeFullWishlistButton';
import { WishlistEmptyState } from './WishlistEmptyState';
import { WishlistProductTile } from './WishlistProductTile';

export interface IWishlistRowsProps {
  inkindRouteId: string;
  skeleton?: boolean;
  skeletonItems?: number;
}

const skelBem = bemBlock(`wishlist-rows-skeleton`);
const Skeleton = ({ skeletonItems = 3 }: Partial<IWishlistRowsProps>): React.ReactElement => (
  <div className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={20} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </div>
);

function WishlistRowsComp({ inkindRouteId }: IWishlistRowsProps): React.ReactElement {
  const { data: inkindPage, error: inkindPageError } = useInkind(inkindRouteId);

  const {
    data: wishListProducts,
    isValidating: isLoadingWishlistProducts,
    error: wishListProductsError,
  } = useWishlistProducts(inkindRouteId);

  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId);

  const maxProductsToDisplay = 6;

  // if (!inkindPage && (inkindPageError || wishListProductsError)) {
  //   return <span className="gik-text-error">{getApiErrorString([inkindPageError, wishListProductsError])}</span>;
  // }

  const tiles = React.useMemo(() => {
    return wishListProducts?.slice(0, maxProductsToDisplay)?.map(item => {
      return (
        <WishlistProductTile
          key={item.id}
          item={item}
          inkindRouteId={inkindRouteId}
          direction="row"
          toolbar={
            canEdit && (
              <RemoveFromWishlistButton
                inkindRouteId={inkindRouteId}
                itemId={item.id}
                child={onClick => (
                  <Tooltip text="REMOVE PRODUCT">
                    <a
                      className={`gik-product-tile__close`}
                      onClick={(ev: React.MouseEvent<HTMLElement>) => {
                        ev.stopPropagation();
                        onClick(ev);
                      }}
                    >
                      <SvgIcon Icon={TrashIcon} size="xs" />
                    </a>
                  </Tooltip>
                )}
              />
            )
          }
        />
      );
    });
  }, [inkindRouteId, canEdit, wishListProducts]);

  if (!wishListProducts || !inkindPage) {
    return <Skeleton />;
  }

  const hasItems = wishListProducts?.length > 0;
  const bem = bemBlock('wishlist-rows');

  const isLoading = !inkindPage || !wishListProducts;
  const isError = isLoading && (inkindPageError || wishListProductsError);

  if (isLoading) {
    return <Skeleton />;
  }
  if (isError) {
    return <Skeleton />;
  }

  return (
    <div className={bem()}>
      {hasItems && tiles}
      {wishListProducts?.length > maxProductsToDisplay && <SeeFullWishlistButton inkindPage={inkindPage} />}
      {!isLoadingWishlistProducts && !hasItems && <WishlistEmptyState inkindRouteId={inkindRouteId} proportion={1.1} />}
    </div>
  );
}

export const WishlistRows = withComponentErrorBoundary(WishlistRowsComp);
